import { type CreateNorwegianDirectDebitAgreement, DirectDebitType } from 'api/directDebitMandate/mappers'
import { useCreateDirectDebitMandateV4, useCreateDirectDebitMandateV8 } from 'api/directDebitMandate/queryHooks'
import { BackOfficeNorwegianDirectDebitAgreementCreateDtoV8AllOf, SupportedCountry } from 'api/swagger/definitions/backoffice'
import useMaterialNotification from 'hooks/useMaterialNotification'
import { CreateMandate } from './countryToMandate'
import type { CreateMandateBankaccountFormValues } from './mandates/Bankaccount'
import type { CreateMandateIBANFormValues } from './mandates/IBAN'
import type { CreateMandateOverforselsServiceFormValues } from './mandates/OverforselsService'

interface CreateDirectDebitMandateSwitchProps {
  customerId: string
  country: SupportedCountry
  onCreateMandate: {
    [SupportedCountry.SE]: (data: CreateMandateBankaccountFormValues) => Promise<unknown>
    [SupportedCountry.FI]: (data: CreateMandateIBANFormValues) => Promise<unknown>
    [SupportedCountry.DK]: (data: CreateMandateOverforselsServiceFormValues) => Promise<unknown>
    [SupportedCountry.NO]: (data: CreateNorwegianDirectDebitAgreement) => Promise<unknown>
  }
  isLoading: boolean
}

export const CreateDirectDebitMandateSwitch = ({
  customerId,
  country,
  isLoading,
  onCreateMandate,
}: CreateDirectDebitMandateSwitchProps) => {
  switch (country) {
    case SupportedCountry.SE:
      return <CreateMandate.SE onCreateMandate={onCreateMandate[SupportedCountry.SE]} isLoading={isLoading} />
    case SupportedCountry.FI:
      return <CreateMandate.FI onCreateMandate={onCreateMandate[SupportedCountry.FI]} isLoading={isLoading} />
    case SupportedCountry.DK:
      return <CreateMandate.DK onCreateMandate={onCreateMandate[SupportedCountry.DK]} isLoading={isLoading} />
    case SupportedCountry.NO:
      return <CreateMandate.NO onCreateMandate={onCreateMandate[SupportedCountry.NO]} isLoading={isLoading} customerId={customerId} />
    case SupportedCountry.NL:
      return <CreateMandate.UNKNOWN />
    case SupportedCountry.EE:
      return <CreateMandate.UNKNOWN />
    case SupportedCountry.LV:
      return <CreateMandate.UNKNOWN />
    case SupportedCountry.DE:
      return <CreateMandate.UNKNOWN />
  }
}

interface CreateDirectDebitMandateProps {
  customerId: string
  country: SupportedCountry
}

export const CreateDirectDebitMandate = ({ customerId, country }: CreateDirectDebitMandateProps) => {
  const createMandateWithAgreement = useCreateDirectDebitMandateV8(customerId)
  const createMandateWithoutAgreement = useCreateDirectDebitMandateV4(customerId)
  const notify = useMaterialNotification()

  const handleSwedenCreateMandate = ({ bankAccount, sendMandateAgreement }: CreateMandateBankaccountFormValues) => {
    if (sendMandateAgreement) {
      return createMandateWithAgreement.mutateAsync({
        type: DirectDebitType.DIRECT_DEBIT_AGREEMENT,
        bankAccount,
      })
    }
    return createMandateWithoutAgreement.mutateAsync({
      type: DirectDebitType.AUTOGIRO,
      bankAccount,
    })
  }

  const handleFinlandCreateMandate = ({ iban }: CreateMandateIBANFormValues) =>
    createMandateWithAgreement.mutateAsync({
      type: DirectDebitType.DIRECT_DEBIT_AGREEMENT,
      iban,
    })

  const handleDenmarkCreateMandate = ({ bankAccount, contactInfo }: CreateMandateOverforselsServiceFormValues) =>
    createMandateWithoutAgreement.mutateAsync({
      type: DirectDebitType.OVERFORSELSSERVICE,
      bankAccount,
      contactInfo: {
        email: contactInfo.email,
        fullName: contactInfo.fullname,
        phoneNumber: contactInfo.phonenumber,
      },
    })

  const handleNorwayCreateMandate = async (data: CreateNorwegianDirectDebitAgreement) => {
    await createMandateWithAgreement.mutateAsync({
      type: DirectDebitType.DIRECT_DEBIT_NORWAY,
      amount: data.amount,
      period: data.period,
      noBankAccount: data.noBankAccount,
      signer: data.signer,
    })

    notify('Direct debit mandate created', 'success')
  }

  return (
    <CreateDirectDebitMandateSwitch
      customerId={customerId}
      country={country}
      isLoading={createMandateWithAgreement.isLoading || createMandateWithoutAgreement.isLoading}
      onCreateMandate={{
        [SupportedCountry.SE]: handleSwedenCreateMandate,
        [SupportedCountry.FI]: handleFinlandCreateMandate,
        [SupportedCountry.DK]: handleDenmarkCreateMandate,
        [SupportedCountry.NO]: handleNorwayCreateMandate,
      }}
    />
  )
}
